import React, { useState, useMemo } from 'react';
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TablePagination, TextField, InputAdornment, Avatar, Chip, IconButton,
  Tooltip, Typography, Paper, Skeleton, TableSortLabel, Menu, MenuItem,
  ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ChevronRight as ChevronRightIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Store as StoreIcon,
  CalendarMonth as CalendarIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as ActivateIcon,
  Block as DeactivateIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const HEAD_COLS = [
  { id: 'name',     label: 'Company',    minWidth: 200 },
  { id: 'contact',  label: 'Contact',    minWidth: 200 },
  { id: 'location', label: 'Location',   minWidth: 200 },
  { id: 'stores',   label: 'Stores',     align: 'center', minWidth: 80 },
  { id: 'users',    label: 'Users',      align: 'center', minWidth: 80 },
  { id: 'status',   label: 'Status',     align: 'center', minWidth: 100 },
  { id: 'subscription', label: 'Subscription', minWidth: 140 },
];

function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';
}

const CompanyTable = ({ companies = [], loading = false, onOpen, onDelete, onActivate, onDeactivate, onEdit }) => {
  const navigate = useNavigate();
  const [search, setSearch]             = useState('');
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(10);
  const [orderBy, setOrderBy]           = useState('subscription');
  const [order, setOrder]               = useState('desc');
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const handleEditClick = (e, company) => {
    e.stopPropagation();
    onEdit?.(company);
  };

  const handleViewClick = (e, company) => {
    e.stopPropagation();
    navigate(`/admin-user-management/company/${company.id}`);
  };

  const handleActionMenuOpen = (e, company) => {
    e.stopPropagation();
    setActionMenuAnchor(e.currentTarget);
    setSelectedCompany(company);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedCompany(null);
  };

  const handleMenuAction = (action) => {
    handleActionMenuClose();
    if (!selectedCompany) return;
    
    switch (action) {
      case 'view':
        navigate(`/admin-user-management/company/${selectedCompany.id}`);
        break;
      case 'edit':
        onEdit?.(selectedCompany);
        break;
      case 'activate':
        onActivate?.(selectedCompany);
        break;
      case 'deactivate':
        onDeactivate?.(selectedCompany);
        break;
      case 'delete':
        onDelete?.(selectedCompany);
        break;
      default:
        break;
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? companies.filter(c =>
          c.name.toLowerCase().includes(q) ||
          (c.btwNumber     || '').toLowerCase().includes(q) ||
          (c.email         || '').toLowerCase().includes(q) ||
          (c.phoneNumber   || '').toLowerCase().includes(q) ||
          (c.fullAddress   || '').toLowerCase().includes(q)
        )
      : companies;

    return [...rows].sort((a, b) => {
      let va, vb;
      
      if (orderBy === 'users') {
        va = a.users.length;
        vb = b.users.length;
      } else if (orderBy === 'stores') {
        va = a.storesCount || 0;
        vb = b.storesCount || 0;
      } else if (orderBy === 'status') {
        va = a.isActive ? 1 : 0;
        vb = b.isActive ? 1 : 0;
      } else if (orderBy === 'subscription') {
        va = a.subscriptionDate ? new Date(a.subscriptionDate).getTime() : 0;
        vb = b.subscriptionDate ? new Date(b.subscriptionDate).getTime() : 0;
      } else {
        va = a[orderBy] ?? '';
        vb = b[orderBy] ?? '';
      }
      
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return order === 'asc' ? cmp : -cmp;
    });
  }, [companies, search, orderBy, order]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(col);
      setOrder('asc');
    }
    setPage(0);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box sx={{
        px: 2.5, py: 1.5, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', borderBottom: '1px solid',
        borderColor: 'divider', bgcolor: 'white',
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          {loading ? 'Loading…' : `${filtered.length} compan${filtered.length !== 1 ? 'ies' : 'y'}`}
        </Typography>
        <TextField
          size="small" placeholder="Search by name, contact, location…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          sx={{ width: 320, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.84rem' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {HEAD_COLS.map(col => (
                <TableCell key={col.id} align={col.align}
                  sx={{
                    fontWeight: 700, fontSize: '0.76rem', bgcolor: '#f7f7f7',
                    minWidth: col.minWidth, textTransform: 'uppercase',
                    letterSpacing: '0.4px', color: 'text.secondary',
                  }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="center" sx={{
                fontWeight: 700, fontSize: '0.76rem', bgcolor: '#f7f7f7',
                textTransform: 'uppercase', letterSpacing: '0.4px',
                color: 'text.secondary', width: 80,
              }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              [...Array(6)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(8)].map((__, j) => (
                    <TableCell key={j}><Skeleton height={18} /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 8, textAlign: 'center' }}>
                  <BusinessIcon sx={{ fontSize: 44, color: 'action.disabled', display: 'block', mx: 'auto', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {search ? 'No companies match your search' : 'No companies found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paged.map(company => (
              <TableRow
                key={company.id || company.name} hover
                onClick={() => navigate(`/admin-user-management/company/${company.id}`)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(102,126,234,0.04)' },
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                {/* Company name + VAT */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{
                      width: 40, height: 40, fontSize: '0.75rem', fontWeight: 800,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    }}>
                      {getInitials(company.name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.4, mb: 0.3 }}>
                        {company.name}
                      </Typography>
                      {company.btwNumber && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ 
                            fontSize: '0.75rem', 
                            color: 'text.secondary',
                            fontWeight: 500,
                          }}>
                            VAT: {company.btwNumber}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </TableCell>

                {/* Contact (Email + Phone) */}
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {company.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                          {company.email}
                        </Typography>
                      </Box>
                    )}
                    {company.phoneNumber && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                          {company.phoneNumber}
                        </Typography>
                      </Box>
                    )}
                    {!company.email && !company.phoneNumber && (
                      <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>—</Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Full Address */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
                    <LocationIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.3, flexShrink: 0 }} />
                    <Typography sx={{ 
                      fontSize: '0.82rem', 
                      lineHeight: 1.5,
                      color: (company.fullAddress && company.fullAddress !== '—') ? 'text.primary' : 'text.secondary',
                    }}>
                      {company.fullAddress || '—'}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Stores count */}
                <TableCell align="center">
                  <Chip
                    icon={<StoreIcon sx={{ fontSize: '13px !important' }} />}
                    label={company.storesCount || 0}
                    size="small"
                    sx={{ fontWeight: 700, bgcolor: 'rgba(255,152,0,0.1)', color: '#ff9800', fontSize: '0.72rem' }}
                  />
                </TableCell>

                {/* Users count */}
                <TableCell align="center">
                  <Chip
                    icon={<PeopleIcon sx={{ fontSize: '13px !important' }} />}
                    label={company.users.length}
                    size="small"
                    sx={{ fontWeight: 700, bgcolor: 'rgba(102,126,234,0.1)', color: '#667eea', fontSize: '0.72rem' }}
                  />
                </TableCell>

                {/* Status */}
                <TableCell align="center">
                  <Chip
                    icon={company.isActive ? <ActivateIcon sx={{ fontSize: '13px !important' }} /> : <DeactivateIcon sx={{ fontSize: '13px !important' }} />}
                    label={company.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    sx={{ 
                      fontWeight: 700, 
                      bgcolor: company.isActive ? 'rgba(76,175,80,0.1)' : 'rgba(158,158,158,0.1)', 
                      color: company.isActive ? '#4caf50' : '#9e9e9e', 
                      fontSize: '0.72rem' 
                    }}
                  />
                </TableCell>

                {/* Subscription Date */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography sx={{ 
                      fontSize: '0.82rem',
                      color: company.subscriptionDate ? 'text.primary' : 'text.secondary',
                    }}>
                      {company.subscriptionDate 
                        ? new Date(company.subscriptionDate).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })
                        : '—'
                      }
                    </Typography>
                  </Box>
                </TableCell>

                {/* Actions */}
                <TableCell align="center" onClick={e => e.stopPropagation()}>
                  <Tooltip title="Actions">
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleActionMenuOpen(e, company)}
                      sx={{ color: '#667eea' }}
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }
        }}
      >
        <MenuItem onClick={() => handleMenuAction('view')} sx={{ py: 1 }}>
          <ListItemIcon>
            <ViewIcon fontSize="small" sx={{ color: '#667eea' }} />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('edit')} sx={{ py: 1 }}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: '#ff9800' }} />
          </ListItemIcon>
          <ListItemText>Edit Company</ListItemText>
        </MenuItem>
        <Divider />
        {!selectedCompany?.isActive && (
          <MenuItem onClick={() => handleMenuAction('activate')} sx={{ py: 1 }}>
            <ListItemIcon>
              <ActivateIcon fontSize="small" sx={{ color: '#4caf50' }} />
            </ListItemIcon>
            <ListItemText>Activate</ListItemText>
          </MenuItem>
        )}
        {selectedCompany?.isActive && (
          <MenuItem onClick={() => handleMenuAction('deactivate')} sx={{ py: 1 }}>
            <ListItemIcon>
              <DeactivateIcon fontSize="small" sx={{ color: '#ff9800' }} />
            </ListItemIcon>
            <ListItemText>Deactivate</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleMenuAction('delete')} sx={{ py: 1, color: '#f44336' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
          </ListItemIcon>
          <ListItemText>Delete Company</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default React.memo(CompanyTable);
